/**
 * Checkout staleness guard — pure helper.
 *
 * Compares a snapshot of a campaign (taken when the storefront cart
 * loaded) against a fresh storefront store read (`GET /storefront/stores/<id>`)
 * and classifies the delta into one of four tiers. The container component (StorefrontCart)
 * uses the tier to decide whether to:
 *   - do nothing (tier === 'none')
 *   - quietly update its derived fields (tier === 'silent')
 *   - re-render the form, prompt the shopper to review (tier === 'form-affecting')
 *   - force a Stripe Elements re-mount + prompt the shopper (tier === 'blocking')
 *
 * Tier rules:
 *   none           -> modified_at unchanged or both campaigns null/equal
 *   silent         -> modified_at differs but no watched field changed
 *   form-affecting -> any watched form field changed (see WATCHED_FORM_FIELDS)
 *   blocking       -> payment_method changed (supersedes form-affecting)
 *
 * Field comparison:
 *   - checkout_fields and dropdown_fields: JSON.stringify (order matters
 *     for render order)
 *   - booleans + payment_method string: strict equality
 *
 * Null/undefined handling: returns 'none' if either side is null/undefined.
 * The first-load case (oldCampaign null) is treated as 'none' — there is
 * no prior state to be stale relative to.
 *
 * NOTE: If you add a new admin-editable field that the shopper cart
 * reads at checkout time, add it to WATCHED_FORM_FIELDS or the
 * blocking-fields handling below, and add a test in
 * checkoutStalenessGuard.test.js. See PR #920 / gate review P1 #1 for
 * the original motivation.
 */

const WATCHED_FORM_FIELDS = [
  "checkout_fields",
  "dropdown_fields",
  "fundraising_enabled",
  "collect_sales_tax",
  "shipping_cost_enabled",
  "coupons_enabled",
];

const ARRAY_OR_OBJECT_FIELDS = new Set(["checkout_fields", "dropdown_fields"]);

function fieldsDiffer(oldCampaign, newCampaign, field) {
  const oldVal = oldCampaign[field];
  const newVal = newCampaign[field];
  if (ARRAY_OR_OBJECT_FIELDS.has(field)) {
    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
  }
  return oldVal !== newVal;
}

export function detectCampaignStaleness(oldCampaign, newCampaign) {
  if (!oldCampaign || !newCampaign) {
    return { tier: "none", changedFields: [] };
  }

  const oldModified = oldCampaign.modified_at;
  const newModified = newCampaign.modified_at;
  if (oldModified === newModified) {
    return { tier: "none", changedFields: [] };
  }

  const changedFields = [];

  // Blocking: payment_method change supersedes everything else
  const paymentMethodChanged =
    oldCampaign.payment_method !== newCampaign.payment_method;
  if (paymentMethodChanged) {
    changedFields.push("payment_method");
  }

  // Form-affecting: collect every watched field that differs
  for (const field of WATCHED_FORM_FIELDS) {
    if (fieldsDiffer(oldCampaign, newCampaign, field)) {
      changedFields.push(field);
    }
  }

  if (paymentMethodChanged) {
    return { tier: "blocking", changedFields };
  }
  if (changedFields.length > 0) {
    return { tier: "form-affecting", changedFields };
  }
  // modified_at differs but no watched field changed
  return { tier: "silent", changedFields: [] };
}
